import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AdminService } from 'src/app/service/admin.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { NotificationService } from 'src/app/service/notification.service';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { MatSort } from '@angular/material/sort';
import { InfoService } from 'src/app/service/info.service';
import { DeletedSubmission } from 'src/app/model/paper';
import { SubmissionHistoryComponent } from 'src/app/component/submission-history/submission-history.component';
import * as dayjs from 'dayjs';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-admin-deleted-submissions',
  templateUrl: './deleted-submissions.component.html',
  styleUrls: ['./deleted-submissions.component.scss']
})
export class AdminDeletedSubmissionsComponent implements OnInit {
  form: FormGroup;
  deletedSubmissions: Array<DeletedSubmission>;
  deletedSubmissionsDataSource = new MatTableDataSource<DeletedSubmission>();
  // position (numbering) primeiro; select por último (transparente, padrão jems3-table).
  deletedTableColumns = ['position', 'id', 'previous-status', 'title', 'event', 'deleted-by', 'deleted-at', 'actions', 'select'];
  selectedStateMap: Map<number, boolean> = new Map();

  /** True enquanto carrega — exibe os cards-placeholder. */
  loading = true;
  /** Texto do filtro (busca em chip no header). */
  search = '';

  @ViewChild(MatSort, { static: false }) set content(sort: MatSort) {
    this.sort = sort;
    if (this.sort) {
      this.deletedSubmissionsDataSource.sort = this.sort;

      this.deletedSubmissionsDataSource.sortingDataAccessor = (submission: DeletedSubmission, header: string) => {
        switch (header) {
          case 'id': return submission.id;
          case 'previous-status': return submission.previousStatus;
          case 'title': return submission.title;
          case 'deleted-at': return submission.deletedAt.toString();
          case 'deleted-by': return submission.deletedBy?.name;
          default: return submission.id;
        }
      };
    }
  }
  sort: MatSort;

  constructor(
    public adminService: AdminService,
    public submissionsService: SubmissionsService,
    public notificationService: NotificationService,
    public dialog: MatDialog,
    public fb: FormBuilder,
    public infoService: InfoService
  ) {
    // Filtro por título / evento / id / autor da exclusão (acento-insensível).
    this.deletedSubmissionsDataSource.filterPredicate = (s: DeletedSubmission, filter: string) => {
      const normalize = (v: any) =>
        (v || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
      const haystack = normalize(
        `${s.id} ${s.title || ''} ${s.event?.acronym || ''} ${s.event?.year || ''} ${s.deletedBy?.name || ''} ${s.previousStatus || ''}`
      );
      return haystack.includes(filter);
    };
  }

  ngOnInit(): void {
    this.getRegisters();
  }

  applyFilter(): void {
    const normalize = (v: any) =>
      (v || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    this.deletedSubmissionsDataSource.filter = normalize(this.search);
  }

  // ─── Seleção ──────────────────────────────────────────────────────────
  getSelectedState(submissionId: number): boolean {
    return this.selectedStateMap.get(submissionId) || false;
  }

  onCheckboxChange(submissionId: number, isSelected: boolean): void {
    this.selectedStateMap.set(submissionId, isSelected);
  }

  toggleAll(checked: boolean): void {
    (this.deletedSubmissions || []).forEach(s => this.selectedStateMap.set(s.id, checked));
  }

  allSelected(): boolean {
    const list = this.deletedSubmissions || [];
    return list.length > 0 && list.every(s => this.getSelectedState(s.id));
  }

  someSelected(): boolean {
    const list = this.deletedSubmissions || [];
    return list.some(s => this.getSelectedState(s.id)) && !this.allSelected();
  }

  hasSelection(): boolean {
    return (this.deletedSubmissions || []).some(s => this.getSelectedState(s.id));
  }

  /** Há ao menos uma selecionada que PODE ser recuperada (trilha não excluída). */
  hasRecoverableSelection(): boolean {
    return (this.deletedSubmissions || []).some(s => this.getSelectedState(s.id) && !s.deletedTrack);
  }

  private getSubmissionsSelected(): number[] {
    return Array.from(this.selectedStateMap.entries())
      .filter(([, selected]) => selected === true)
      .map(([id]) => id);
  }

  // ─── Exclusão permanente ──────────────────────────────────────────────
  permanentlyDeleteSubmission(submission_id: number, delete_reason: string): void {
    this.submissionsService.permanentlyDeleteSubmission(submission_id, delete_reason).subscribe(() => {
      this.deletedSubmissions = this.deletedSubmissions.filter(submission => submission.id !== submission_id);
      this.deletedSubmissionsDataSource.data = this.deletedSubmissions;
      this.selectedStateMap.delete(submission_id);
    });
  }

  /** Apaga permanentemente as selecionadas (barra flutuante) — confirma antes. */
  confirmDeleteSelected(): void {
    const ids = this.getSubmissionsSelected();
    if (!ids.length) { return; }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.deleted-submissions.confirm-delete-selected-title',
        content: 'admin.deleted-submissions.confirm-delete-selected-content',
        params: { content: { count: ids.length } }
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        ids.forEach(id => this.permanentlyDeleteSubmission(id, ''));
      }
    });
  }

  /** Recupera as selecionadas (barra flutuante). Pula as de trilha excluída
   *  (não recuperáveis); confirma antes. */
  recoverSelected(): void {
    const recoverable = (this.deletedSubmissions || [])
      .filter(s => this.getSelectedState(s.id) && !s.deletedTrack);
    if (!recoverable.length) { return; }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.deleted-submissions.confirm-recover-selected-title',
        content: 'admin.deleted-submissions.confirm-recover-selected-content',
        params: { content: { count: recoverable.length } }
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      forkJoin(recoverable.map(s => this.submissionsService.recoverySubmission(s.id, s.previousStatus))).subscribe(
        () => {
          this.notificationService.notify('admin.deleted-submissions.notify.done');
          this.getRegisters();
        },
        () => { this.getRegisters(); }
      );
    });
  }

  permanentlydeleteSubmissionsOlderThanThreeMonths(): void {
    const old = this.deletedSubmissions.filter(s => s.deletedAt < dayjs().subtract(3, 'month'));
    old.forEach(s => this.permanentlyDeleteSubmission(s.id, ''));
  }

  getRegisters(): void {
    this.loading = true;
    this.submissionsService.getDeletedSubmissions().subscribe(
      (deletedSubmissions) => {
        this.deletedSubmissions = deletedSubmissions.sort((a, b) => a.deletedAt.isBefore(b.deletedAt) ? 1 : -1);
        this.deletedSubmissionsDataSource.data = this.deletedSubmissions;
        this.loading = false;
      },
      () => { this.loading = false; }
    );
  }

  getHistory(id: number): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = '55rem';
    dialogConfig.height = '30rem';
    dialogConfig.panelClass = 'my-custom-dialog-class';
    dialogConfig.data = { id };
    this.dialog.open(SubmissionHistoryComponent, dialogConfig);
  }

  recoverySubmission(submissionId: number, status: string): void {
    const data = {
      title: 'admin.deleted-submissions.notify.dialog-title',
      content: ''
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.submissionsService.recoverySubmission(submissionId, status).subscribe(
          () => {
            this.notificationService.notify('admin.deleted-submissions.notify.done');
            this.getRegisters();
          },
          () => {
            this.getRegisters();
          }
        );
      }
    });
  }
}
